import { useFetch } from '#imports'
import qs from 'qs'
import useXFetch from '~/composables/useXFetch'
import { cookieAttribution, storageReferer } from '~/middleware/attribution.global'
import { API_ITEMS_UPPER_LIMIT, DOMAIN_LIST } from '~/types/constants'
import { FileOrder, RestoreStatus, ShareStatus, SupportLocale } from '~/types/enum'
import { type ActivityRichInfo } from '~/utils/richJson'
import { ADMIN_API_V1, CONFIG_API_V1, DRIVE_API_V1, PP_DRIVE_API, PP_USER_API, VIP_API_V1 } from './common'

export const enum EVipRegionType {
  GLOBAL = 'global',
  REGIONAL = 'regional',
}

export interface IVipInfo {
  user_id: string
  status: string // 会员状态，‘ok’表示会员可用
  expire: string // 会员过期时间
  type: string // 会员类型， platinum(白金)
  fee_record: string // 会员付费记录类型：no_record(没有付费记录)""(异常返回空)  xxx(其他)
  vipItem: {
    description: string
    expire: string
    status: string
    surplus_day: number // 会员剩余会员天数
    type: EVipRegionType
  }[]
  extUserInfo: {
    userRegion: EVipRegionType // 用户当前所属分区
  }
  restricted: {
    result: boolean // 是否受限
  }
}

export const enum ThumbnailSize {
  SIZE_SMALL = 'SIZE_SMALL',
  SIZE_LARGE = 'SIZE_LARGE',
}

export { DRIVE_API_V1 }

export interface GetShareQuery {
  share_id: string
  limit?: number
  pass_code?: string
  pass_code_token?: string
  thumbnail_size?: ThumbnailSize
  page_token?: string
  folders_first?: boolean
  order?: Exclude<FileOrder, FileOrder.NAME_ASC | FileOrder.NAME_DESC>
}

/**
 * http://drive.office.k8s.xunlei.cn/?urls.primaryName=share.swagger.json#/ShareService/ShareService_GetShare
 * 获取分享详情
 */
export const getShare = async ({ limit = 1e2, thumbnail_size = ThumbnailSize.SIZE_LARGE, ...query }: GetShareQuery) =>
  useXFetch<Share>(`${DRIVE_API_V1}/share?${qs.stringify({ limit, thumbnail_size, ...query })}`)

export interface GetShareDetailQuery extends Omit<GetShareQuery, 'pass_code'> {
  parent_id: string
}

/**
 * http://drive.office.k8s.xunlei.cn/?urls.primaryName=share.swagger.json#/ShareService/ShareService_GetShareDetail
 * 获取子文件夹的信息，不需要登录
 */
export const getShareDetail = ({
  limit = 1e2,
  thumbnail_size = ThumbnailSize.SIZE_LARGE,
  ...query
}: GetShareDetailQuery) =>
  useXFetch<Pick<Share, 'share_status' | 'share_status_text' | 'files' | 'next_page_token'>>(
    `${DRIVE_API_V1}/share/detail?${qs.stringify({
      limit,
      thumbnail_size,
      ...query,
    })}`,
  )

/**
 * http://drive.office.k8s.xunlei.cn/?urls.primaryName=share.swagger.json#/ShareService/ShareService_GetShareFileInfo
 * 获取文件完整信息，包括播放链接
 */
export const getShareFileInfo = (query: { share_id: string; file_id: string; pass_code_token?: string }) =>
  useXFetch<{
    share_status: ShareStatus
    file_info: PPFile
    share_status_text: string
  }>(`${DRIVE_API_V1}/share/file_info?${qs.stringify(query)}`)

export interface RestoreShareBody {
  share_id: string
  pass_code_token: string
  file_ids?: string[]
  ancestor_ids?: string[]
  params?: { trace_file_ids: string } // {"VMyu3FOxTqiL7bw0c2NFO8dLo0":"VN-sBfvlQYOE-QH9DV_5qkJ4o0"}
}

/**
 * http://drive.office.k8s.xunlei.cn/?urls.primaryName=share.swagger.json#/ShareService/ShareService_RestoreShare
 * 转存分享文件
 * ancestor_ids 说明
 * 1. 转存全部文件，不需要传入file_ids，也不需要传入ancestor_ids
 * 2. 转存第一层的文件，例如A+B，file_ids传A、B，不需要传入ancestor_ids
 * 3. 转存第二层的文件，例如A.1, file_ids传A.1，ancestor_ids传A
 * 4. 转存第三层的文件，例如A1.1.1,  file_ids传A.1.1，ancestor_ids传A、A.1
 */
export const restoreShare = (body: RestoreShareBody) =>
  useXFetch<{
    shareStatus: ShareStatus
    file_id: string
    restore_status: RestoreStatus
    restore_task_id: string
    params?: { trace_file_ids: string } // string[].join(',')
  }>(`${DRIVE_API_V1}/share/restore`, {
    method: 'POST',
    body,
    withCredentials: true,
  })
export const getVipPrivilege = async () =>
  useXFetch<{
    data: {
      expire: string
      status: 'ok' | 'invalid'
      type: string
      user_id: string
    }
  }>(`${DRIVE_API_V1}/privilege/vip`, { withCredentials: true })

// 获取网盘空间使用情况
export const getStorageUsage = async () =>
  useXFetch<{ quota: { usage: number; limit: number } }>(`${DRIVE_API_V1}/about`, { withCredentials: true })

// 获取用户信息
interface UserMe {
  email: string
  name: string
  password: string
  phone_number?: string
  picture: string
  status: string
  sub: string
}

export const getUserInfo = async () => useXFetch<UserMe>(`${PP_USER_API}/v1/user/me`, { withCredentials: true })

/* 新用户赠送免费天数 */
export const presetFreeDays = () => {
  const { host, pathname } = window.location
  return useXFetch<{ free_days: number }>(`${VIP_API_V1}/activity/invite`, {
    method: 'POST',
    body: {
      from: 'web',
      rr: host + pathname,
      tf: cookieAttribution.get().source,
      rf: storageReferer.get().join(',') || undefined,
    },
    withCredentials: true,
  })
}
// 使用兑换码兑换会员
export const redeemCDK = async (cdKey: string) =>
  useXFetch(`${VIP_API_V1}/order/activation-code`, {
    method: 'POST',
    body: {
      activation_code: cdKey || '',
      data: {},
    },
    withCredentials: true,
  })
/* 获取当前访问的 IP 以及所在的区域 */
export const getAreaCountryCode = () =>
  useXFetch<{ data: { countryCode: string; ip: string } }>(`${DRIVE_API_V1}/privilege/area_country_code`, {
    withCredentials: false,
    withCaptcha: false,
  })

/**
 * http://drive.office.k8s.xunlei.cn/?urls.primaryName=share.swagger.json#/ShareService/ShareService_BatchGetShareFileInfo
 * 批量获取文件的详细
 */
export const getBatchFileInfo = (body: { share_id: string; file_ids: string[]; pass_code_token: string }) => {
  const url = `${DRIVE_API_V1}/share:batch_file_info`
  return useXFetch<{
    shares_status: ShareStatus
    files: PPFile[]
  }>(url, { method: 'POST', body })
}

/**
 * http://drive.office.k8s.xunlei.cn/?urls.primaryName=task.swagger.json#/Tasks/Tasks_GetTask
 * 使用 task_id 获取任务详情
 */
export const getTask = (taskId: string) => useXFetch<Task>(`${DRIVE_API_V1}/tasks/${taskId}`, { withCredentials: true })

export interface IpBlockRes {
  accessible: boolean
  prompt: {
    title: string
    body: string
    redirect: {
      title: string
      url: string
    }[]
  }
}

/* 获取当前访问 IP 是否被屏蔽 */
export const getIpBlock = (domain: (typeof DOMAIN_LIST)[number]) =>
  useXFetch<IpBlockRes>(`https://access.${domain}/access_controller/v1/area_accessible`, {
    withCredentials: false,
    withCaptcha: false,
  })

export interface ExternalProductsPrice {
  result: string
  values: {
    external_products: {
      google_product?: {
        price: string
      }
      mega_product?: {
        price: string
      }
      pikpak_product?: {
        price: string
        sale_price: string
      }
    }
  }
}

export const getExternalProductsPrice = async (language: string) =>
  useXFetch<ExternalProductsPrice>(`${CONFIG_API_V1}/external_products`, {
    method: 'POST',
    body: {
      data: {
        language,
      },
    },
  })

export interface HomeSpeedAndFileData {
  average_speed: number
  total_speed: number
  task_count: number
}

export const getHomeSpeedAndFileData = async (isServer: boolean) =>
  useFetch<Array<HomeSpeedAndFileData>>(`${PP_DRIVE_API}/statistics/pub/download`, {
    method: 'get',
    server: isServer,
  })

export interface HomeMediaResource {
  result: string
  values: {
    resources: {
      content_facebook: string
      content_magnet: string
      content_tiktok: string
      content_torrent: string
      content_twitter: string
      description_facebook: string
      description_magnet: string
      description_tiktok: string
      description_torrent: string
      description_twitter: string
    }
  }
}

export const getHomeMediaResource = async (language: string) =>
  useXFetch<HomeMediaResource>(`${CONFIG_API_V1}/resources`, {
    method: 'POST',
    body: {
      // client: 'web', // FIXME 全局配置系统有问题，暂不启用
      data: { language },
    },
  })

// http://drive.office.k8s.xunlei.cn/?urls.primaryName=resource.swagger.json#/ResourceResolver/ResourceResolver_CreateList
export const resolveResource = (urls: string) =>
  useXFetch<Resource>(`${DRIVE_API_V1}/resource/list`, {
    method: 'POST',
    body: {
      urls,
      page_size: API_ITEMS_UPPER_LIMIT,
      thumbnail_type: 'FROM_HASH',
    },
    headers: { 'X-Request-From': window.location.origin + window.location.pathname },
    withCredentials: true,
  })

type TemplateType = 'KR' | 'US' | 'BLACK_FRIDAY'

export type ActivityType = {
  deepLink?: string
  image?: string
  desc?: string
  color?: string
  hover_color?: string
}
export type ActivityIconTypeKR = {
  banner: ActivityType
  naver: ActivityType
  naver_disa: ActivityType
}

export type ActivityIconTypeUS = {
  banner: ActivityType
  list: ActivityType[]
}

interface ActivityOperationKR {
  kakao_fan_group: ActivityType
  operation_gift: {
    icons: ActivityIconTypeKR[]
    h5_icons: ActivityIconTypeKR[]
  }
}

interface ActivityOperationBF {
  operation_banner: {
    deepLink?: string
    image?: string
  }
}

interface ActivityOperationUS {
  sidebar_entrance: {
    material: ActivityType
    h5_material: ActivityType
  }
  operation_material: {
    material: ActivityIconTypeUS
    h5_material: ActivityIconTypeUS
  }
  // template: 'US';
}

export interface ActivityRes {
  result: string
  values: {
    activity_operation: ActivityOperationKR &
      ActivityOperationUS &
      ActivityOperationBF & { template: TemplateType; user_page_language_allow?: SupportLocale } & {
        activity_top_bar?: {
          invitation_page?: ActivityRichInfo
          invited_page?: ActivityRichInfo
          share_page?: ActivityRichInfo
        }
      }
  }
}

export const getActivityOperation = (lang: string, userLang: string) =>
  useXFetch<ActivityRes>(`${CONFIG_API_V1}/activity_operation`, {
    method: 'POST',
    body: {
      client: 'web',
      data: {
        language_system: lang,
        language_app: userLang,
      },
      withCredentials: true,
    },
  })

// 获取任务状态（用来判断是否新用户）
export const getTaskStatus = (scene: string) => {
  const urlParams = new URLSearchParams()
  urlParams.set('scene', scene)

  const url = `${PP_DRIVE_API}/vip/v1/task/status?${urlParams.toString()}`
  return useXFetch(url, {
    method: 'GET',
    withCredentials: true,
  })
}

export interface DebugOptions {
  values: {
    debug_options: {
      allow_vconsole?: boolean
    }
  }
}

export const getVconsoleGlobalConfig = (data: { userid?: string; uuid?: string }) =>
  useXFetch<DebugOptions>(`${CONFIG_API_V1}/debug_options`, {
    method: 'POST',
    body: {
      client: 'web',
      data,
    },
  })

interface BlockFilesParams {
  share_user_id: string // 当前用户的 id
  gcids: string[]
  file_ids: string[]
}

export const blockSharedFiles = (params: BlockFilesParams) =>
  useXFetch(`${ADMIN_API_V1}/share`, {
    method: 'POST',
    body: {
      share_user_id: params.share_user_id,
      gcids: params.gcids,
      file_ids: params.file_ids,
    },
    withCredentials: true,
  })

interface RewardVipResponseData {
  updated: boolean
  add_days: number
  err_no_match_signin_condition?: string
  reward_coupon?: {
    coupon_type: 'DISCOUNT_MONTHLY' | 'DISCOUNT_YEARLY' // 月度 / 年度
    discount: number
    expires: number // 过期时间戳
  }
}

export const rewardVip = (type: string, data?: string) =>
  useXFetch<RewardVipResponseData>(`${VIP_API_V1}/activity/rewardVip`, {
    method: 'POST',
    body: {
      type,
      data,
    },
    withCredentials: true,
  })

// 退订邮件
export const unsubscribe = (d: string) => {
  const urlParams = new URLSearchParams()
  urlParams.set('d', d)

  const url = `${PP_DRIVE_API}/mail/v1/unsubscribe?${urlParams.toString()}`
  return useXFetch<{ error?: string; unsubscribe?: boolean }>(url, {
    method: 'GET',
  })
}

interface InviteRes {
  total_user: number
  extra_space: number
  month: Month
  year: Month
}

interface Month {
  nums: number
  extra_upload: number
  data: Datum[]
}

interface Datum {
  time: string
  invited_user: string
  invited_user_name: string
  invited_user_avatar: string
  size: number
  status: string
}

// 获取邀请者获得的额外奖励记录
export const getInviteExtActivityPage = (limit: string) => {
  const urlParams = new URLSearchParams()
  // urlParams.set('begin', begin)
  urlParams.set('limit', limit)

  const url = `${PP_DRIVE_API}/vip/v1/activity/inviteExtActivityPage?${urlParams.toString()}`
  return useXFetch<InviteRes>(url, {
    method: 'GET',
    withCredentials: true,
  })
}

export const getBasicConfig = (language: string) =>
  useXFetch<{
    values: {
      basic: {
        share_code_tips: boolean // 是否显示设置中的“直接显示搜索时发现的分享”
        claim_now_page_iframe_src: string
        enable_claim_now_activity: boolean
      }
    }
  }>(`${CONFIG_API_V1}/basic`, {
    method: 'POST',
    body: {
      client: 'web',
      data: {
        language_system: import.meta.client ? navigator.language : language,
        language_app: language,
      },
    },
    withCredentials: true,
  })

export type TaskVipType = 'sign_in'

/**
 * google验证码校验结果验证
 * @param params captcha_token - google验证码reCAPTCHA token
 * @returns
 */
export const verifyRecaptchaToken = (params: { type: TaskVipType; captcha_token: string }) => {
  const url = `${PP_DRIVE_API}/vip/v1/verifyRecaptchaToken`
  return useXFetch(url, {
    method: 'POST',
    body: params,
    withCredentials: true,
  })
}

export const enum CompensateActivityId {
  /** 阿里云机房事故 */
  ALI_YUN_ACCIDENT_202409 = 'aliyun_accident_202409',
  /** 2024 黑色星期五活动 */
  BLACK_FRIDAY_2024 = 'compensationBlackFriday2024',
}

interface CompensateInterface {
  activity_id: CompensateActivityId
  check?: boolean
}

/** 查询是否有领取补偿的资格 */
export const checkActivityCompensate = (params: CompensateInterface) => {
  const url = `${PP_DRIVE_API}/vip/v1/activity/compensate`
  return useXFetch<{ days: number }>(url, {
    method: 'POST',
    body: params,
    withCredentials: true,
  })
}
